// useInactivityLogout
// -------------------
// The idle-session effect extracted out of portal.jsx: after `warnMs` of no
// activity it auto-saves any in-progress draft and shows the expiry warning;
// after `logoutMs` it logs the user out. Any of the listed user-activity events
// resets both timers. The periods are now admin-configurable per audience
// (Manage → System Setup → Config; resolved in portal.jsx via
// services/sessionTimeout.ts) and passed in; they DEFAULT to the historical
// 25-min warn / 30-min logout when not supplied.
//
// TypeScript source -- useInactivityLogout.js is a thin re-export stub.

import { type MutableRefObject, useEffect } from "react";

export interface InactivityLogoutDeps {
  user: unknown;
  saveDraftRef: MutableRefObject<(() => void) | null>;
  onLogout: () => void;
  setSessionWarning: (v: boolean) => void;
  // Optional overrides (ms). Absent → the historical defaults below.
  warnMs?: number;
  logoutMs?: number;
}

const DEFAULT_WARN_MS = 25 * 60 * 1000;
const DEFAULT_LOGOUT_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keypress", "touchstart", "scroll", "click"];

export function useInactivityLogout(deps: InactivityLogoutDeps): void {
  const { user, saveDraftRef, onLogout, setSessionWarning } = deps;
  const warnMs = deps.warnMs && deps.warnMs > 0 ? deps.warnMs : DEFAULT_WARN_MS;
  const logoutMs = deps.logoutMs && deps.logoutMs > 0 ? deps.logoutMs : DEFAULT_LOGOUT_MS;
  useEffect(
    () => {
      if (!user) return;
      let warnTimer: ReturnType<typeof setTimeout>;
      let logoutTimer: ReturnType<typeof setTimeout>;
      function resetTimer() {
        clearTimeout(warnTimer);
        clearTimeout(logoutTimer);
        setSessionWarning(false);
        warnTimer = setTimeout(() => {
          // Auto-save the draft if the user is mid-form.
          if (saveDraftRef.current) {
            try {
              saveDraftRef.current();
            } catch (_e) {}
          }
          setSessionWarning(true);
        }, warnMs);
        logoutTimer = setTimeout(() => {
          onLogout();
        }, logoutMs);
      }
      for (const e of ACTIVITY_EVENTS) {
        window.addEventListener(e, resetTimer);
      }
      resetTimer();
      return () => {
        clearTimeout(warnTimer);
        clearTimeout(logoutTimer);
        for (const e of ACTIVITY_EVENTS) {
          window.removeEventListener(e, resetTimer);
        }
      };
    },
    // Re-run on user change OR when the configured periods change (so a late
    // config load / an admin edit re-arms the timers). onLogout /
    // setSessionWarning / saveDraftRef are stable enough across renders and
    // useExhaustiveDependencies is disabled project-wide.
    [user, warnMs, logoutMs]
  );
}
